<template>
  <div class="chart-container">
    <Line :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup>
import { Line } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale } from 'chart.js'
import { computed } from 'vue'

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale)

const props = defineProps({
  monthlyStats: {
    type: Array,
    required: true
  }
})

const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

const chartData = computed(() => {
  // Trouver l'année la plus récente
  const latestYear = Math.max(...props.monthlyStats.map(item => item.year))
  
  // Créer un dataset complet pour l'année
  const fullYearData = Array(12).fill().map((_, index) => {
    const month = (index + 1).toString().padStart(2, '0')
    const monthData = props.monthlyStats.find(item => 
      item.year === latestYear && item.month === month
    )
    
    return monthData || {
      year: latestYear,
      month,
      stats: {
        pending: { count: 0 },
        pending_onarrival: { count: 0 },
        confirmed: { count: 0 },
        partially_paid: { count: 0 },
        cancelled: { count: 0 },
        payment_failed: { count: 0 },
        expired: { count: 0 }
      }
    }
  })

  // Labels pour tous les mois
  const labels = fullYearData.map(item => monthNames[parseInt(item.month) - 1])

  return {
    labels,
    datasets: [
      {
        label: 'En attente',
        data: fullYearData.map(item => item.stats.pending?.count || 0),
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 1,
        tension: 0.4,
        fill: true
      },
      {
        label: 'Paiement sur place',
        data: fullYearData.map(item => item.stats.pending_onarrival?.count || 0),
        borderColor: '#D4AF37',
        backgroundColor: 'rgba(212, 175, 55, 0.1)',
        borderWidth: 1,
        tension: 0.4,
        fill: true
      },
      {
        label: 'Confirmées',
        data: fullYearData.map(item => item.stats.confirmed?.count || 0),
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 1,
        tension: 0.4,
        fill: true
      },
      {
        label: 'Partiellement payées',
        data: fullYearData.map(item => item.stats.partially_paid?.count || 0),
        borderColor: '#D4AF37',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 1,
        tension: 0.4,
        fill: true
      },
      {
        label: 'Annulées',
        data: fullYearData.map(item => item.stats.cancelled?.count || 0),
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 1,
        tension: 0.4,
        fill: true
      },
      {
        label: 'Échec paiement',
        data: fullYearData.map(item => item.stats.payment_failed?.count || 0),
        borderColor: '#8B5CF6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderWidth: 1,
        tension: 0.4,
        fill: true
      },
      {
        label: 'Expirées',
        data: fullYearData.map(item => item.stats.expired?.count || 0),
        borderColor: '#9CA3AF',
        backgroundColor: 'rgba(156, 163, 175, 0.1)',
        borderWidth: 1,
        tension: 0.4,
        fill: true
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || ''
          const value = context.raw || 0
          return `${label}: ${value} réservations`
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      }
    },
    y: {
      beginAtZero: true,
      suggestedMax: 10, // Ajuster selon vos données
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
        drawBorder: false
      },
      ticks: {
        precision: 0,
        stepSize: 1
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  }
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
}
</style>